import React from 'react';

const NotificationModal = ({ 
  isOpen, 
  onClose, 
  type = 'error', // 'error', 'warning', 'success', 'info'
  title, 
  message,
  // Action button props (e.g., "Log In" instead of "Got it")
  actionLabel,
  onAction,
  // Confirmation mode props
  isConfirmation = false,
  onConfirm,
  confirmText = 'Yes, Delete',
  cancelText = 'Cancel'
}) => {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'error':
        return {
          icon: '💔',
          bgGradient: 'from-rose-50 via-pink-25 to-champagne-50',
          borderColor: 'border-rose-200',
          iconBg: 'from-rose-400 via-pink-400 to-gold-400',
          titleGradient: 'from-rose-600 via-pink-600 to-purple-600',
          messageColor: 'text-gray-700'
        };
      case 'warning':
        return {
          icon: '⚠️',
          bgGradient: 'from-rose-50 via-pink-25 to-champagne-50',
          borderColor: 'border-gold-200',
          iconBg: 'from-gold-400 via-yellow-400 to-rose-400',
          titleGradient: 'from-rose-600 via-pink-600 to-purple-600',
          messageColor: 'text-gray-700'
        };
      case 'success':
        return {
          icon: '💕',
          bgGradient: 'from-rose-50 via-pink-25 to-champagne-50',
          borderColor: 'border-emerald-200',
          iconBg: 'from-emerald-400 via-pink-400 to-rose-400',
          titleGradient: 'from-rose-600 via-pink-600 to-purple-600',
          messageColor: 'text-gray-700'
        };
      case 'info':
        return {
          icon: '✨',
          bgGradient: 'from-rose-50 via-pink-25 to-champagne-50',
          borderColor: 'border-purple-200',
          iconBg: 'from-purple-400 via-pink-400 to-rose-400',
          titleGradient: 'from-rose-600 via-pink-600 to-purple-600',
          messageColor: 'text-gray-700'
        };
      default:
        return getTypeStyles('error');
    }
  };

  const styles = getTypeStyles();

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
      <div className={`bg-gradient-to-br ${styles.bgGradient} backdrop-blur-lg rounded-3xl shadow-2xl border-2 ${styles.borderColor}/50 max-w-md w-full mx-4 animate-scaleIn relative overflow-hidden`}>
        
        {/* Floating sparkles background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-pulse opacity-20"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 1}s`
              }}
            >
              <span className="text-gold-400 text-sm">✨</span>
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="relative z-10 flex items-center justify-between p-6 pb-4">
          <div className="flex items-center space-x-4">
            <div className={`w-16 h-16 bg-gradient-to-r ${styles.iconBg} rounded-2xl flex items-center justify-center shadow-2xl animate-bounce`}>
              <span className="text-3xl animate-pulse">{styles.icon}</span>
            </div>
            <div>
              <h3 className={`text-2xl font-serif font-bold bg-gradient-to-r ${styles.titleGradient} bg-clip-text text-transparent mb-1`}>
                {title || (type === 'error' ? 'Oops!' : type === 'warning' ? 'Heads Up!' : type === 'success' ? 'Wonderful!' : 'Just So You Know')}
              </h3>
              {/* Elegant underline */}
              <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-rose-600 transition-colors duration-300 p-2 rounded-full hover:bg-white/50 hover:shadow-lg transform hover:scale-110"
          >
            <span className="text-2xl font-bold">×</span>
          </button>
        </div>

        {/* Content */}
        <div className="relative z-10 px-6 pb-6">
          <p className={`${styles.messageColor} leading-relaxed font-light text-lg`}>
            {message}
          </p>
        </div>

        {/* Footer */}
        <div className="relative z-10 px-6 pb-6 flex justify-end gap-4">
          {isConfirmation ? (
            <>
              {/* Cancel Button */}
              <button
                onClick={onClose}
                className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-2xl font-bold hover:bg-gray-50 hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                {cancelText}
              </button>
              {/* Confirm Button */}
              <button
                onClick={() => {
                  onConfirm && onConfirm();
                  onClose();
                }}
                className="group relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white px-8 py-3 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <div className="flex items-center">
                  <span className="mr-2 text-xl">🗑️</span>
                  {confirmText}
                </div>
                {/* VowAI signature glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
              </button>
            </>
          ) : (
            <button
              onClick={() => {
                if (onAction) onAction();
                onClose();
              }}
              className="group bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white px-8 py-3 rounded-2xl font-bold text-lg hover:from-rose-600 hover:via-pink-600 hover:to-gold-600 hover:shadow-2xl transform hover:scale-110 transition-all duration-300 shadow-xl"
            >
              <div className="flex items-center">
                <span className="mr-2 text-xl group-hover:animate-bounce">{actionLabel ? '🔐' : '💖'}</span>
                {actionLabel || 'Got it'}
              </div>
            </button>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { 
            opacity: 0;
            transform: scale(0.8);
          }
          to { 
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default NotificationModal;
